import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  LinkIcon,
  UsersIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  UserPlusIcon,
  TrophyIcon,
  WalletIcon,
  ShoppingBagIcon,
  BuildingLibraryIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
  StarIcon,
  DevicePhoneMobileIcon,
  BuildingOffice2Icon,
  FilmIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { ELEVATE_HISPANO_SLUG } from '../../utils/elevateEvent';

const sidebarVariants = {
  expanded: { width: 256 },
  collapsed: { width: 80 }
};

function Sidebar({ activeView, setActiveView, onLogout, notificationsCount, isCollapsed, setIsCollapsed, userType = 'user', menuBadges = {}, onMobileNavigate }) {
  const { user } = useAuth();
  const { t, formatMessage } = useLanguage();
  const navigate = useNavigate();

  const isElevateHispano = user?.company_slug === ELEVATE_HISPANO_SLUG;

  let menuItems = [
    { 
      id: 'dashboard', 
      label: t.enrola?.giftsHome ? t.enrola.giftsHome : t.sidebar.dashboard, 
      icon: HomeIcon
    },
    { 
      id: 'analytics', 
      label: t.sidebar.analytics, 
      icon: ChartBarIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForClient: true,
    },
    { 
      id: 'referrals', 
      label: t.enrola?.programs ? t.enrola.programs : t.sidebar.referrals, 
      icon: LinkIcon,
      hideForAdmin: true,
      hideForVendor: true
    },
    { 
      id: 'network', 
      label: t.sidebar.network, 
      icon: UsersIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForClient: true,
    },
    { 
      id: 'rewards', 
      label: t.sidebar.rewards, 
      icon: TrophyIcon,
      hideForAdmin: true,
      hideForVendor: true,
    },
    {
      id: 'giftsInTransit',
      label: t.sidebar.giftsInTransit,
      icon: GiftIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForElevateHispano: true,
    },
    {
      id: 'videoHub',
      label: t.sidebar.videoHub,
      icon: FilmIcon,
      hideForAdmin: true,
      hideForVendor: true,
    },
    {
      id: 'contacts',
      label: t.sidebar.myContacts,
      icon: DevicePhoneMobileIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForClient: true,
    },
    {
      id: 'wallet',
      label: t.enrola?.wallet || 'Wallet',
      icon: WalletIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForClient: true,
    },
    {
      id: 'club',
      label: t.enrola?.club || 'Shopping club',
      icon: ShoppingBagIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForClient: true,
    },
    {
      id: 'corporate',
      label: t.enrola?.corporate || 'Corporate',
      icon: BuildingLibraryIcon,
      hideForAdmin: true,
      hideForVendor: true,
      hideForClient: true,
    }
  ];

  const isClientUser = !user?.is_admin && !user?.is_vendor && userType !== 'vendor';
  const isPlatformAdmin = user?.is_admin && !user?.company_id;
  menuItems = menuItems.filter((item) => {
    if (user?.is_admin && item.hideForAdmin) return false;
    if ((user?.is_vendor || userType === 'vendor') && item.hideForVendor) return false;
    if (isClientUser && item.hideForClient) return false;
    if (isElevateHispano && item.hideForElevateHispano) return false;
    return true;
  });

  // Platform super-admin menu
  if (isPlatformAdmin) {
    menuItems = [
      {
        id: 'dashboard',
        label: t.sidebar.dashboard,
        icon: HomeIcon,
      },
      {
        id: 'companies',
        label: t.platformAdmin?.sidebarCompanies || 'Companies',
        icon: BuildingOffice2Icon,
      },
      {
        id: 'users',
        label: t.sidebar.users,
        icon: UsersIcon,
      },
      {
        id: 'clients',
        label: t.platformAdmin?.sidebarClients || 'Clients',
        icon: UserGroupIcon,
      },
      {
        id: 'analytics',
        label: t.platformAdmin?.sidebarSales || t.sidebar.analytics,
        icon: ChartBarIcon,
      },
      {
        id: 'prospects',
        label: t.sidebar.prospects,
        icon: ClipboardDocumentListIcon,
      },
      {
        id: 'rewards',
        label: t.sidebar.rewards,
        icon: TrophyIcon,
      },
      {
        id: 'pendingGifts',
        label: t.sidebar.pendingGifts,
        icon: GiftIcon,
      },
      {
        id: 'vendorReviews',
        label: t.sidebar.vendorReviews,
        icon: StarIcon,
      },
      {
        id: 'videoHub',
        label: t.sidebar.videoHub,
        icon: FilmIcon,
      },
      {
        id: 'backups',
        label: t.sidebar.backups || 'Backups',
        icon: CircleStackIcon,
      },
    ];
  } else if (user?.is_admin) {
    menuItems = [
      { 
        id: 'dashboard', 
        label: t.sidebar.dashboard, 
        icon: HomeIcon
      },
      {
        id: 'users',
        label: t.sidebar.users,
        icon: UsersIcon
      },
      {
        id: 'analytics',
        label: t.sidebar.analytics,
        icon: ChartBarIcon
      },
      {
        id: 'importUsers',
        label: t.sidebar.importUsers,
        icon: UserPlusIcon
      },
      {
        id: 'prospects',
        label: t.sidebar.prospects,
        icon: ClipboardDocumentListIcon
      },
      {
        id: 'rewards',
        label: t.sidebar.rewards,
        icon: TrophyIcon,
      },
      {
        id: 'pendingGifts',
        label: t.sidebar.pendingGifts,
        icon: GiftIcon,
        hideForElevateHispano: true,
      },
      {
        id: 'vendorReviews',
        label: t.sidebar.vendorReviews,
        icon: StarIcon,
      },
      {
        id: 'enrola',
        label: t.enrola?.enrolaHub || 'Enrola',
        icon: SparklesIcon
      },
      {
        id: 'videoHub',
        label: t.sidebar.videoHub,
        icon: FilmIcon
      }
    ];
  }

  // Vendor-specific menu items
  if (userType === 'vendor' || user?.is_vendor) {
    menuItems = [
      { 
        id: 'dashboard', 
        label: t.sidebar.dashboard, 
        icon: HomeIcon
      },
      {
        id: 'clients',
        label: t.sidebar.myClients,
        icon: UserGroupIcon
      },
      {
        id: 'prospects',
        label: t.sidebar.prospects,
        icon: ClipboardDocumentListIcon
      },
      {
        id: 'partners',
        label: t.sidebar.partners || 'Partner network',
        icon: UserGroupIcon
      },
      {
        id: 'referrals',
        label: t.enrola?.promoCard || 'Promo & links',
        icon: LinkIcon
      },
      {
        id: 'pendingGifts',
        label: t.sidebar.pendingGifts,
        icon: GiftIcon,
        hideForElevateHispano: true,
      },
      {
        id: 'analytics',
        label: t.sidebar.analytics,
        icon: ChartBarIcon
      },
      {
        id: 'videoHub',
        label: t.sidebar.videoHub,
        icon: FilmIcon
      }
    ];
  }

  // Role-specific menus replace the base list — re-apply Elevate Hispano hides.
  if (isElevateHispano) {
    menuItems = menuItems.filter((item) => !item.hideForElevateHispano);
    if (!menuItems.some((item) => item.id === 'club')) {
      const clubItem = {
        id: 'club',
        label: t.enrola?.club || 'Shopping club',
        icon: ShoppingBagIcon,
      };
      const dashboardIdx = menuItems.findIndex((item) => item.id === 'dashboard');
      menuItems.splice(dashboardIdx >= 0 ? dashboardIdx + 1 : 0, 0, clubItem);
    }
    if (isClientUser && !menuItems.some((item) => item.id === 'promotions')) {
      const promotionsItem = {
        id: 'promotions',
        label: t.promotions?.title || 'Promotions',
        icon: TrophyIcon,
      };
      const dashboardIdx = menuItems.findIndex((item) => item.id === 'dashboard');
      menuItems.splice(dashboardIdx >= 0 ? dashboardIdx + 1 : 0, 0, promotionsItem);
    }
    if (user?.is_admin && !isPlatformAdmin && !menuItems.some((item) => item.id === 'promotions')) {
      const promotionsItem = {
        id: 'promotions',
        label: t.promotions?.adminTitle || t.promotions?.title || 'Promotions',
        icon: TrophyIcon,
      };
      const rewardsIdx = menuItems.findIndex((item) => item.id === 'rewards');
      menuItems.splice(rewardsIdx >= 0 ? rewardsIdx + 1 : menuItems.length, 0, promotionsItem);
    }
  }

  const handleNavClick = (item) => {
    if (onMobileNavigate && window.innerWidth < 1024) {
      onMobileNavigate();
    }

    if (user?.is_admin) {
      const path =
        item.id === 'dashboard'
          ? '/admin'
          : item.id === 'settings'
            ? '/admin/settings'
            : `/admin/${
              item.id === 'importUsers'
                ? 'import-users'
                : item.id === 'pendingGifts'
                  ? 'pending-gifts'
                  : item.id === 'vendorReviews'
                    ? 'vendor-reviews'
                    : item.id === 'videoHub'
                      ? 'video-hub'
                      : item.id === 'club'
                        ? 'club'
                        : item.id
            }`;
      navigate(path);
    } else if (user?.is_vendor || userType === 'vendor') {
      const path =
        item.id === 'dashboard'
          ? '/vendor'
          : item.id === 'pendingGifts'
            ? '/vendor/pending-gifts'
            : item.id === 'videoHub'
              ? '/vendor/video-hub'
              : item.id === 'club'
                ? '/vendor/club'
                : `/vendor/${item.id}`;
      navigate(path);
    } else {
      setActiveView(item.id);
    }
  };

  const bottomItems = [
    { 
      id: 'settings', 
      label: t.sidebar.settings, 
      icon: Cog6ToothIcon
    }
  ];

  const logoutButton = (
    <motion.button
      type="button"
      onClick={onLogout}
      aria-label={isCollapsed ? t.sidebar.logout : undefined}
      className="w-full min-w-0 flex items-center overflow-hidden px-4 py-3 rounded-2xl text-secondary-600 hover:bg-error-50 hover:text-error-700 transition-[color,background-color,box-shadow,transform] duration-300 group   "
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <ArrowRightOnRectangleIcon className="w-5 h-5 text-error-500" />

      <AnimatePresence>
        {!isCollapsed && (
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="ml-3 min-w-0 truncate text-sm font-medium"
          >
            {t.sidebar.logout}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );

  return (
    <motion.div
      id="app-sidebar"
      variants={sidebarVariants}
      animate={isCollapsed ? 'collapsed' : 'expanded'}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="shell-sidebar safe-top safe-bottom fixed left-0 top-0 z-[9999] flex h-[100dvh] flex-col shadow-elegant "
    >
      {/* Toggle Button */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? (t.common?.expandSidebar || 'Expand sidebar') : (t.common?.collapseSidebar || 'Collapse sidebar')}
        aria-expanded={!isCollapsed}
        className="absolute -right-3 top-8 z-20 hidden h-7 w-7 items-center justify-center rounded-full bg-white/95 text-secondary-400 shadow-soft ring-1 ring-orange-100/60 transition-[color,box-shadow,transform] duration-500 ease-out hover:scale-105 hover:text-orange-600 hover:shadow-glow lg:flex"
      >
        {isCollapsed ? (
          <ChevronRightIcon className="w-3.5 h-3.5" />
        ) : (
          <ChevronLeftIcon className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Header */}
      <div className="overflow-x-hidden px-5 pt-5 pb-3.5">
        <div className="flex min-h-10 w-full flex-col items-center justify-center">
          <AnimatePresence mode="wait">
            {isCollapsed ? (
              <motion.img
                key="sidebar-icon"
                src="/icono.png"
                alt={t.brand?.logoAlt || 'Enrola by Elantar'}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="h-10 w-10 flex-shrink-0 object-contain"
              />
            ) : (
              <motion.img
                key="sidebar-logo"
                src="/logo.png"
                alt={t.brand?.logoAlt || 'Enrola by Elantar'}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="mx-auto h-9 w-auto max-w-[200px] object-contain object-center    "
              />
            )}
          </AnimatePresence>
          {!isCollapsed && user?.company_name ? (
            <motion.p
              key="sidebar-company"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
              className="mt-1.5 w-full max-w-[200px] truncate text-center text-[11px] font-medium text-secondary-500  "
            >
              {formatMessage(t.sidebar.byCompany, { company: user.company_name })}
            </motion.p>
          ) : null}
        </div>
      </div>

      <div
        className={`sidebar-brand-divider ${isCollapsed ? 'mx-3' : 'mx-5'}`}
        role="presentation"
        aria-hidden="true"
      />

      {/* Navigation */}
      <nav className="min-h-0 flex-1 space-y-2 overflow-x-hidden overflow-y-auto overscroll-x-none px-4 pb-4 pt-3">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          const badgeCount = menuBadges[item.id] ?? 0;

          return (
            <React.Fragment key={item.id}>
              <motion.button
                type="button"
                onClick={() => handleNavClick(item)}
                aria-current={isActive ? 'page' : undefined}
                aria-label={isCollapsed ? item.label : undefined}
                className={`flex min-w-0 items-center rounded-2xl transition-[color,background-color,border-color,box-shadow,transform] duration-500 ease-out relative group ${
                  isCollapsed ? 'mx-auto size-12 justify-center px-0 py-0' : 'w-full gap-3 px-3 py-2.5'
                } ${
                  isActive 
                    ? 'bg-gradient-to-r from-orange-500/10 to-amber-500/10 text-orange-700 shadow-soft   ' 
                    : 'text-secondary-600 hover:bg-white/50 hover:text-secondary-900   '
                }`}
                whileHover={{ scale: 1.008 }}
                whileTap={{ scale: 0.995 }}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-2xl border border-orange-200/50 "
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                
                <span className={`relative z-10 transition-transform duration-300 group-hover:scale-110 ${isActive ? 'nav-icon-active' : 'nav-icon-inactive'}`}>
                  <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-secondary-500 '}`} />
                  {badgeCount > 0 && (
                    <div className="absolute -top-1.5 -right-1.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-gradient-to-r from-error-500 to-error-600 px-0.5 shadow-glow">
                      <span className="text-[9px] font-bold leading-none text-white">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    </div>
                  )}
                </span>
                
                <AnimatePresence>
                  {!isCollapsed && (
                <motion.span
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="relative z-10 min-w-0 truncate text-sm font-medium"
                >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Bottom Actions */}
      <div className={`min-w-0 overflow-x-hidden space-y-2 border-t border-orange-100/60 ${isCollapsed ? 'px-2.5 py-4' : 'p-4'}`}>
        {bottomItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;

          return (
            <motion.button
              key={item.id}
              type="button"
              onClick={() => handleNavClick(item)}
              aria-current={isActive ? 'page' : undefined}
              aria-label={isCollapsed ? item.label : undefined}
              className={`w-full min-w-0 flex items-center overflow-hidden px-4 py-3 rounded-2xl transition-[color,background-color,border-color,box-shadow,transform] duration-300 relative group ${
                isActive 
                  ? 'bg-gradient-to-r from-orange-500/10 to-amber-500/10 text-orange-700   ' 
                  : 'text-secondary-600 hover:bg-white/50 hover:text-secondary-900   '
              }`}
              whileHover={{ scale: 1.008 }}
              whileTap={{ scale: 0.995 }}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${isActive ? 'text-orange-600 ' : 'text-secondary-500 '}`} />
                {item.badge && item.badge > 0 && (
                  <div className="absolute -top-2 -right-2 w-5 h-5 bg-gradient-to-r from-error-500 to-error-600 rounded-full flex items-center justify-center shadow-glow">
                    <span className="text-2xs font-bold text-white">{item.badge}</span>
                  </div>
                )}
              </div>
              
              <AnimatePresence>
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="ml-3 min-w-0 truncate text-sm font-medium"
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          );
        })}

        {/* Logout Button */}
        {logoutButton}
      </div>
    </motion.div>
  );
}

export default Sidebar;
